<template>
  <div
    ref="el"
    class="header"
    :class="[
      backgroundClass,
      {
        'is-top-reached': headerState.topReached,
        'is-collapsed': headerState.collapsed,
        'is-lp': isLandingPage,
        'is-transparent': themeState.isTransparent,
      },
    ]">
    <div class="header-wrapper">
      <div class="header-base">
        <div class="container-full">
          <div class="header-main">
            <div class="logo" :class="{ 'fill-white': themeState.isDark }">
              <!-- Logo Animation tbd -->
              <LogoSvg />
              <!-- {{currentModuleState.theme}} -->
            </div>

            <div class="container-right">
              <div v-if="blok.top_is_language_switch_enabled || isLandingPage" class="language-selector">
                <ul>
                  <li
                    v-for="locale in languages"
                    :key="locale.id"
                    class="action-large-regular"
                    :class="{ 'is-active': localeLanguage === locale.id }"
                    :aria-label="locale.label"
                    @click="changeLanguage(locale.id)">
                    {{ locale.id }}
                  </li>
                </ul>
              </div>

              <div v-if="blok.top_right_links?.length" class="hero-link action-large-bold">
                <UtilButton
                  v-if="blok.top_right_links[0].component === 'util_button'"
                  :isBackgroundDark="themeState.isDark"
                  :isSmallOnMobile="true"
                  :blok="blok.top_right_links[0]" />

                <SharedBaseLink v-else :link="blok.top_right_links?.[0].link" class="primary-link">
                  {{ blok.top_right_links?.[0].text }}
                </SharedBaseLink>
              </div>

              <!-- Burger trigger tbd -->
              <a
                v-if="!blok.top_is_language_switch_enabled && !isLandingPage"
                class="burger"
                href="#"
                aria-label="Toggle navigation"
                :class="{ 'is-x': headerState.menuOpen }"
                @click.prevent="() => (headerState.menuOpen = !headerState.menuOpen)">
                <div class="burger-rect"></div>
                <div class="burger-rect"></div>
              </a>
            </div>

            <!-- Nav layer -->
            <BMNavLayer v-if="headerState.menuOpen" :blok="blok" />
          </div>
        </div>

        <div class="container-full header-sub" :class="{ 'is-open': subMenuOpen }">
          <!-- Center container for in page anchors -->
          <template v-if="inPageBlok?.length">
            <!-- Submenu -->
            <div v-if="inPageBlok[0].anchors?.length" class="sub-menu" :class="{ 'is-open': subMenuOpen }">
              <ul>
                <li
                  v-for="(item, index) in inPageBlok[0].anchors"
                  :key="'anchor' + index"
                  class="sub-item action-large-regular">
                  <SharedBaseLink :link="item.link" class="primary-link" :class="{ 'is-active': false }">
                    {{ item.text }}
                  </SharedBaseLink>
                </li>
              </ul>
            </div>

            <p
              class="page-title action-large-bold"
              @click="
                () => {
                  if (isMobile) subMenuOpen = !subMenuOpen;
                }
              ">
              <span class="page-title-label">
                {{ inPageBlok[0].text }}
              </span>

              <BaseIcon
                name="fi_chevron-bottom"
                class="mobile-sub-icon"
                :size="16"
                color="inherit"
                :class="{ 'is-open': subMenuOpen }" />
            </p>

            <div class="container-right">
              <template v-if="inPageBlok[0].call_to_actions?.length">
                <UtilButton
                  :isSmallOnMobile="true"
                  :isBackgroundDark="themeState.isDark"
                  :blok="inPageBlok[0].call_to_actions[0]" />
              </template>
            </div>
          </template>
        </div>
      </div>
      <WaveSubSvg class="header-wave" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useState } from '#app';
import { useCurrentModule, useLocale } from '#imports';
import gsap from 'gsap';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ISbAlternateObject } from '@storyblok/vue';
import { templateRef } from '@vueuse/core';
import { SharedBaseLink } from '@seb-platform/shared/components/base';
import { useScroll, useWindow } from '@seb-platform/shared/composables';
import { normalizeUrl } from '@seb-platform/shared/utils/url';
import LogoSvg from '~/assets/svg/logo_onlight.svg?skipsvgo';
import WaveSubSvg from '~/assets/svg/schaerer_wave_submenu.svg?skipsvgo';
import { BaseIcon } from '~/components/base';
import { useHeader } from '~/composables/useHeader';
import type { Locale, MarketLocale } from '~/types/locales';
import type { BmHeaderInPageStoryblok, BmHeaderRichStoryblok } from '~/types/storyblok-generated';
import UtilButton from '../../utils/UtilButton/UtilButton.vue';
import BMNavLayer from '../BMNavLayer/BMNavLayer.vue';

const { locales, localeLanguage, localeMarket } = useLocale();
const languages = locales.value
  .flatMap(entry => entry.languages || [])
  .filter(lang => !!lang.label && !!lang.id)
  .reduce((acc: { id: string; label: string }[], lang: { id: string; label: string }) => {
    if (!acc.some(item => item.id === lang.id)) {
      acc.push(lang);
    }
    return acc;
  }, [])
  .sort((a, b) => a.label.localeCompare(b.label));

const props = defineProps<{
  blok: BmHeaderRichStoryblok;
  inPageBlok?: BmHeaderInPageStoryblok[];
  alternates?: ISbAlternateObject[];
}>();

const { state: scrollState } = useScroll();
const { state: headerState } = useHeader();
const { state: windowState } = useWindow();

const { state: currentModuleState } = useCurrentModule();

const themeState = reactive({
  isDark: false,
  isTransparent: true,
});

const route = useRoute();
const router = useRouter();

const el = templateRef('el');

const isMobile = useState<boolean>(() => false);
const subMenuOpen = useState<boolean>(() => false);

const currentNavigationHeight = ref(0);

const currentScroll = {
  top: 0,
  threshold: 0,
  collapseThreshold: 0,
};

/* helper methods */
const changeLanguage = (language: string) => {
  //check if market has language
  const market = locales.value.find(m => m.id === String(localeMarket.value));
  if (market) {
    const languageInMarket = market.languages.find(l => l.id === language);
    const newLocale = {
      market: languageInMarket ? localeMarket.value : undefined,
      language: language,
    };

    if (!newLocale.market) {
      //fallback market with language
      const fallback = locales.value.find(entry => entry.languages?.some(lang => lang.id === language));
      newLocale.market = fallback?.id as MarketLocale | undefined;
    }

    const lMarket = `${newLocale.language}_${newLocale.market}` as Locale;
    const url = normalizeUrl(lMarket);

    void router.push(url);
  }
};

const updateHeaderTransparency = (scrollingUp: boolean, subMenuToggled: boolean = false) => {
  if (!subMenuOpen.value && scrollState.top <= 40) {
    themeState.isTransparent = true;
    return;
  }

  if (scrollState.top > 40) {
    themeState.isTransparent = false;
    return;
  }

  if (subMenuToggled) {
    themeState.isTransparent = false;
    return;
  }

  if ((isMobile.value && scrollState.top > 40) || subMenuOpen.value) {
    themeState.isTransparent = false;
    return;
  } else if ((isMobile.value && scrollState.top <= 40) || !subMenuOpen.value) {
    themeState.isTransparent = true;
    return;
  }

  if (scrollState.top < (isMobile.value ? 40 : 80) && scrollingUp) {
    themeState.isTransparent = true;
  } else if (scrollingUp || hasSubMenu.value) {
    themeState.isTransparent = false;
  }
};

const hasSubMenu = computed(() => {
  return !!props.inPageBlok?.length;
});

const isLandingPage = computed(() => {
  return !!props.inPageBlok?.length;
});

const backgroundClass = computed(() => {
  let background = 'is-dark';

  if (subMenuOpen.value) {
    background = currentModuleState.theme === 'brown-light' ? 'is-light' : 'is-dark';
    return background;
  }

  // if (currentModuleState.name === 'cm_stage') return 'is-dark';

  // if (currentModuleState.name === 'cm_media') return 'is-transparent'; ???
  if (currentModuleState.theme === 'brown-light') return 'is-light';
  if (currentModuleState.theme === 'white') return 'is-light';
  if (currentModuleState.theme === 'schaerer-red') return 'is-dark';

  return background;
});

watch(
  () => backgroundClass.value,
  () => {
    themeState.isDark =
      backgroundClass.value === 'is-dark' || backgroundClass.value === 'is-transparent' ? true : false;
  }
);

/* size calculation for text overflow */
watch(
  () => windowState.width,
  () => {
    isMobile.value = windowState.width >= 1024 ? false : true;
    currentNavigationHeight.value = windowState.width >= 1024 ? 0 : el.value.offsetHeight;
  }
);

/* animates sub-menu on mobile */
watch(
  () => subMenuOpen.value,
  () => {
    updateHeaderTransparency(false, true);

    if (!isMobile.value) return;

    const subMenu = el.value.querySelectorAll('.sub-menu') as NodeListOf<HTMLElement>;
    const subMenuItems = el.value.querySelectorAll('.sub-menu li') as NodeListOf<HTMLElement>;

    const targetHeight = subMenuOpen.value ? 'auto' : '1px';
    gsap.to(subMenu, { duration: 0.8, transformOrigin: '0 0', ease: 'expo.out', height: targetHeight });

    if (subMenuOpen.value && window.innerWidth < 1024) {
      gsap.to(subMenuItems, { duration: 0.25, y: 0, autoAlpha: 1, delay: 0.25, stagger: 0.1 });
    } else {
      gsap.to(subMenuItems, { delay: -0.2, stagger: -0.05, duration: 0.2, y: 0, overwrite: true, autoAlpha: 0 });
    }
  }
);

/* reverts submobile styles when changing breakpoint */
watch(
  () => isMobile.value,
  () => {
    if (!isMobile.value) {
      subMenuOpen.value = false;

      const subMenu = el.value.querySelectorAll('.sub-menu') as NodeListOf<HTMLElement>;
      const subMenuChevron = el.value.querySelectorAll('.sub-menu svg') as NodeListOf<HTMLElement>;
      const subMenuItems = el.value.querySelectorAll('.sub-menu li') as NodeListOf<HTMLElement>;

      gsap.set(subMenu, { clearProps: 'all' });
      gsap.set(subMenuChevron, { clearProps: 'all' });
      gsap.set(subMenuItems, { clearProps: 'all' });
    }
  }
);

/* scroll */
watch(
  () => scrollState.top,
  () => {
    const UP_THRESHOLD = isMobile.value ? 20 : 50;
    const COLLAPSE_THRESHOLD = 150;

    const scrollingUp = currentScroll.top - scrollState.top > 0;

    if (scrollingUp) currentScroll.threshold += currentScroll.top - scrollState.top;
    else currentScroll.threshold = 0;

    currentScroll.collapseThreshold += currentScroll.top - scrollState.top;

    headerState.collapsed = !(currentScroll.threshold > UP_THRESHOLD || scrollState.top <= (isMobile.value ? 20 : 40));
    currentScroll.top = scrollState.top;

    headerState.topReached = scrollState.top <= 40;

    if (Math.abs(currentScroll.collapseThreshold) > COLLAPSE_THRESHOLD) {
      currentScroll.collapseThreshold = 0;
      subMenuOpen.value = false;
    }

    updateHeaderTransparency(scrollingUp);
  }
);

/* hide navlayer when route changes */
watch(route, () => (headerState.menuOpen = false));

onMounted(() => {
  headerState.hasTabs = !!props.inPageBlok?.length;
});
</script>

<style src="./BMHeader.scss" lang="scss" scoped />
